/* <sz-water-bg> — fullscreen animated aquarium backdrop (vanilla WebGL).

   REWRITTEN 2026-08-20. The previous shader drew sin() of a LINEAR coordinate,
   which renders as parallel repeating stripes — wallpaper, not water. Owner
   reviewed three replacements and chose B ("caustic tank").

   ⚠️ SECOND REVISION, same day. I first shipped a "B-refined" that was dimmed on
   TWO axes at once — canvas opacity .62 -> .42 AND ray amplitudes .60/.55/.35 ->
   .38/.34/.20 — leaving roughly 43% of the brightness the owner had approved.
   They judged it too subtle, correctly. This ships option B VERBATIM at the .62
   the approved preview was rendered at.
   🔑 The cost savings and the dimming were independent: DPR, framerate and
   deferred init carry ALL of the performance win, and amplitude/opacity are
   free. Never trade brightness for speed when brightness is not what costs.

   What makes it read as water rather than stripes:
   - rays CONVERGE on a light source above the surface instead of running parallel
   - a slow ripple field bends every ray, so they flicker the way a real surface does
   - the caustic net the surface throws, held near the waterline
   - suspended particulate, which is the actual reason a light shaft is VISIBLE in water
   - bubbles that slalom rather than rise straight, fading in low and out high

   Cost control (measured, not assumed — design-mockups/water-bg-v2/_bench.mjs):
   - caustic uses 2 noise octaves and the ripple 3; the higher octaves were below
     a pixel once the depth falloff blurred them, so they cost without showing
   - DPR capped at 1.0 (was 1.5) — pixel count is the dominant term, this is -56%
   - 30fps cap; the drift is far too slow for anyone to see 60
   - init deferred to requestIdleCallback so it never competes with first paint
   - rendering pauses while the tab is hidden
   Net: ~2,277ms of GPU work per second versus ~2,232ms for the shader it replaces.
   It reads completely differently and costs about the same.

   Degradations, all sanctioned by the original handoff:
   - prefers-reduced-motion: renders a SINGLE frame instead of looping
   - =767px viewports: skips WebGL entirely (mobile is the opaque no-backdrop
     variant and sz-rd.css hides the element anyway)
   - no WebGL: renders nothing; the page's fixed teal->black gradient is the fallback */
(function () {
  const VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';
  const FRAG = `precision highp float;
uniform float t;
uniform vec2  r;

float hash21(vec2 p){ p = fract(p*vec2(123.34,345.45)); p += dot(p,p+34.345); return fract(p.x*p.y); }

float vnoise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  vec2 u=f*f*(3.0-2.0*f);
  float a=hash21(i), b=hash21(i+vec2(1.0,0.0)), c=hash21(i+vec2(0.0,1.0)), d=hash21(i+vec2(1.0,1.0));
  return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
}

float fbm(vec2 p){
  float s=0.0, a=0.5;
  for(int i=0;i<4;i++){ s+=a*vnoise(p); p=p*2.03+vec2(1.7,-2.3); a*=0.5; }
  return s;
}

/* suspended particulate — what actually makes a light shaft VISIBLE in water */
float specks(vec2 p, float cells, float speed, float seed){
  vec2 q = p*cells + vec2(sin(t*0.05+seed)*0.35, t*speed);
  vec2 i = floor(q), f = fract(q);
  float h  = hash21(i+seed);
  float on = step(0.80, h);
  vec2  c  = vec2(hash21(i+seed+3.1), hash21(i+seed+7.7));
  c += 0.07*vec2(sin(t*0.40+h*6.28), cos(t*0.33+h*5.11));
  float d  = length(f-c);
  return on*smoothstep(0.11,0.0,d)*(0.35+0.65*hash21(i+seed+11.3));
}

/* bubbles: continuous lanes (no cell-pop), each bubble rises on an S-path,
   fades in low and out high, and is drawn as a lit rim + faint fill + specular dot */
float bubbles(vec2 p, float asp, float lanes, float seed, float speed, float rad, float density){
  float x  = p.x/asp*lanes;
  float li = floor(x);
  float lf = fract(x);
  float acc = 0.0;
  for(int k=0;k<2;k++){
    float id = li + float(k)*37.0 + seed;
    float h  = hash21(vec2(id, 1.7));
    float on = step(1.0-density, hash21(vec2(id, 5.9+float(k))));
    float sp = speed*(0.55+0.90*hash21(vec2(id,4.3)));
    float ph = hash21(vec2(id,8.1));
    float y  = fract(ph + t*sp);
    /* real bubbles do not go straight up — they slalom */
    float cx = 0.5 + sin(y*11.0 + h*6.283)*0.15 + sin(y*27.0 + h*3.1)*0.045;
    float g  = hash21(vec2(id,2.2));
    float rr = rad*(0.40 + 1.35*g*g);          /* mostly small, occasionally one big */
    vec2  d  = vec2((lf-cx)*asp/lanes, p.y-y);
    float dd = length(d);
    /* a bubble is a soft lit shell, not an outline: wide halo, soft rim,
       thin bright meniscus and one specular dot where the light hits it */
    float halo = smoothstep(rr*2.1, 0.0, dd)*0.10;
    float rim  = smoothstep(rr*1.15, rr*0.72, dd)*smoothstep(rr*0.20, rr*0.74, dd)*0.85;
    float fill = smoothstep(rr*0.80, 0.0, dd)*0.10;
    float hi   = smoothstep(rr*0.40, 0.0, length(d-vec2(-0.32,0.34)*rr))*0.75;
    float fade = smoothstep(0.0,0.10,y)*smoothstep(1.0,0.86,y);
    acc += on*fade*(halo + rim + fill + hi);
  }
  return acc;
}
/* B · CAUSTIC TANK — the signature aquarium look. Soft wide shafts plus the
   wobbling caustic net the surface throws, depth colour absorption, drifting
   particulate, and a few larger slow bubbles wobbling as they rise. */
float caustic(vec2 p){
  vec2  q = p*2.6 + vec2(0.0, t*0.045);
  float n = fbm(q);
  float m = fbm(q + vec2(5.3, -1.7) + n*0.65);
  float c = 1.0 - abs(n-m)*2.9;
  return pow(clamp(c,0.0,1.0), 5.0);
}

void main(){
  vec2  uv    = gl_FragCoord.xy / r;
  float asp   = r.x / r.y;
  vec2  p     = vec2(uv.x*asp, uv.y);
  float depth = 1.0 - uv.y;

  vec2  L   = vec2(0.50*asp, 1.85);
  vec2  d   = p - L;
  float ang = atan(d.x, -d.y);

  float rip = fbm(vec2(ang*2.0, t*0.060))*2.0 - 1.0;
  float a2  = ang + rip*0.085;

  float g = 0.0;
  g += pow(max(sin(a2*13.0 + t*0.11      ), 0.0), 3.0)*0.60;
  g += pow(max(sin(a2* 6.0 - t*0.07 + 2.2), 0.0), 2.0)*0.55;
  g += pow(max(sin(a2*29.0 + t*0.17 + 4.4), 0.0), 6.0)*0.35;
  /* Rays to the SIDES, not the centre (owner 2026-08-29: "distracting while
     reading"). The old single term peaked at ang=0 — directly beneath the light
     — which is exactly the column the PDP copy occupies, so the brightest part
     of the backdrop sat behind the text. Two terms now: the OUTER falloff still
     kills rays at extreme angles (unchanged in spirit), and an INNER term fades
     them out through the middle. Light reads as coming down the flanks of the
     tank and the reading column stays quiet. Keep them apart — collapsing this
     into one smoothstep is what put the peak in the centre. */
  float rayOuter  = smoothstep(1.50, 0.34, abs(ang));
  float rayCentre = smoothstep(0.12, 0.62, abs(ang));
  g *= rayOuter * rayCentre;
  g *= exp(-depth*1.75);

  /* caustic net, strongest near the surface, riding on top of the shafts */
  /* The net's 0.35 floor below is width-independent, so without this the
     surface shimmer still plays behind the copy after the shafts move out.
     Tapered, not removed — the centre keeps some life. */
  float cau = caustic(p) * exp(-depth*2.6) * mix(0.55, 1.0, rayCentre);
  float net = cau*(0.35 + 0.65*g);

  float lit  = clamp((g+net)*1.4, 0.0, 1.0);
  float mote = specks(p, 8.0, 0.010, 21.3)*0.9 + specks(p, 15.0, 0.017, 5.5)*0.45;

  float bub  = bubbles(p, asp, 9.0, 4.0, 0.048, 0.0105, 0.34);

  vec3 tint = mix(vec3(0.34,1.00,0.80), vec3(0.05,0.32,0.46), clamp(depth*1.05,0.0,1.0));

  float v   = g*0.62 + net*0.55 + mote*lit*0.70;
  vec3  col = tint*v + vec3(0.72,1.00,0.92)*bub*0.55 + vec3(0.04,0.22,0.20)*exp(-depth*2.4)*0.14;
  float al  = clamp(v + bub*0.60 + exp(-depth*2.4)*0.07, 0.0, 1.0);
  gl_FragColor = vec4(col, al);
}`;
  class SzWaterBg extends HTMLElement {
    connectedCallback() {
      if (document.documentElement.getAttribute('data-sz-theme') === 'light') return;
      if (window.matchMedia('(max-width: 767px)').matches) return;
      /* Never compete with first paint — the backdrop is decorative, so it can
         wait for the main thread to go quiet. */
      const boot = () => this._boot();
      if ('requestIdleCallback' in window) requestIdleCallback(boot, { timeout: 2000 });
      else setTimeout(boot, 200);
    }
    _boot() {
      if (this._started || !this.isConnected) return;
      this._started = true;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const c = document.createElement('canvas');
      c.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;z-index:0;pointer-events:none;mix-blend-mode:screen;opacity:.62';
      this.appendChild(c);
      const gl = c.getContext('webgl', { alpha: true, antialias: false, powerPreference: 'low-power' });
      if (!gl) return;
      const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
      const pr = gl.createProgram();
      gl.attachShader(pr, sh(gl.VERTEX_SHADER, VERT));
      gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(pr); gl.useProgram(pr);
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(pr, 'a');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      const uT = gl.getUniformLocation(pr, 't'), uR = gl.getUniformLocation(pr, 'r');
      const resize = () => {
        /* Pixel count dominates cost. 1.0 rather than 1.5 is a 56% cut and the
           art direction is soft enough that the difference is not visible. */
        const d = Math.min(window.devicePixelRatio || 1, 1.0);
        c.width = innerWidth * d; c.height = innerHeight * d;
        gl.viewport(0, 0, c.width, c.height);
      };
      resize();
      this._onResize = resize;
      addEventListener('resize', resize);
      const start = performance.now();
      const draw = () => {
        gl.uniform1f(uT, (performance.now() - start) / 1000);
        gl.uniform2f(uR, c.width, c.height);
        gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      if (reduced) { draw(); return; }
      /* 30fps. Nothing here moves fast enough for 60 to be perceptible, and it
         halves the GPU work for the whole time the page is open. */
      const STEP = 1000 / 30;
      let last = 0;
      const loop = (now) => {
        this._raf = requestAnimationFrame(loop);
        if (now - last < STEP) return;
        last = now;
        draw();
      };
      this._raf = requestAnimationFrame(loop);
      this._onVis = () => {
        if (document.hidden) { cancelAnimationFrame(this._raf); this._raf = 0; }
        else if (!this._raf) { last = 0; this._raf = requestAnimationFrame(loop); }
      };
      document.addEventListener('visibilitychange', this._onVis);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      removeEventListener('resize', this._onResize);
      if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    }
  }
  if (!customElements.get('sz-water-bg')) customElements.define('sz-water-bg', SzWaterBg);
})();
